import React from 'react';
import { DICCIONARIOS, Clave, Idioma, Texto } from './textos';

const LLAVE_GUARDADA = 'razapro.idioma';

/**
 * Idioma de la interfaz.
 *
 * Arranca en ESPAÑOL salvo que el navegador diga otra cosa. No al revés: los
 * usuarios de esta app son contratistas latinos, y la landing les promete
 * "todo en español". Un inglés por defecto obligaría a la mayoría a buscar un
 * selector que no sabe que existe.
 */
export function detectarIdioma(): Idioma {
    if (typeof window === 'undefined') return 'es';
    const guardado = window.localStorage?.getItem(LLAVE_GUARDADA);
    if (guardado === 'es' || guardado === 'en') return guardado;
    const nav = window.navigator?.language?.toLowerCase() ?? '';
    return nav.startsWith('en') ? 'en' : 'es';
}

/**
 * ¿Ya eligió idioma alguna vez esta persona?
 *
 * `detectarIdioma()` no sirve para saberlo: siempre devuelve un idioma, así que
 * no distingue «lo eligió» de «se lo adivinamos por el navegador». La landing
 * necesita esa diferencia para preguntar UNA vez y no volver a molestar.
 *
 * Vive aquí y no en quien pregunta para que la llave `razapro.idioma` no ande
 * escrita a mano en dos sitios: el día que cambie, cambia sola.
 */
export function hayIdiomaGuardado(): boolean {
    if (typeof window === 'undefined') return true;
    const g = window.localStorage?.getItem(LLAVE_GUARDADA);
    return g === 'es' || g === 'en';
}

/** Rellena `{marcadores}` y elige singular o plural. */
export function traducir(
    idioma: Idioma,
    clave: Clave,
    vars?: Record<string, string | number>,
): string {
    const entrada: Texto = DICCIONARIOS[idioma][clave] ?? DICCIONARIOS.es[clave];
    let txt: string;
    if (typeof entrada === 'string') {
        txt = entrada;
    } else {
        // El plural mira `count`. Ojo: en español y en inglés, 0 va en plural
        // ("0 clientes", "0 clients") — solo el 1 exacto es singular.
        const n = Number(vars?.count ?? 0);
        txt = n === 1 ? entrada.uno : entrada.otros;
    }
    if (!vars) return txt;
    return txt.replace(/\{(\w+)\}/g, (_, k) => String(vars[k] ?? `{${k}}`));
}

/** El tipo de `t`, exportado aparte. Hace falta para las funciones sueltas —
 *  las que no son componentes y por tanto no pueden llamar al hook— que
 *  reciben `t` por parámetro en vez de buscarlo por su cuenta. */
export type Traducir = (clave: Clave, vars?: Record<string, string | number>) => string;

/**
 * El locale para `toLocaleDateString` y compañía.
 *
 * Va aquí, junto al idioma, porque el fallo estaba justo en esa separación:
 * el texto pasaba por `t()` y las FECHAS no, así que el calendario decía
 * «August 2026» y «SUN MON TUE» con la app entera en español y con
 * `lang="es"` puesto en el documento. Nadie escribió ese inglés a mano — sale
 * de un `'en-US'` clavado dentro del `toLocaleDateString`.
 *
 * `es-MX` y no `es-ES`: es una app para contratistas latinos en Estados
 * Unidos. Cambia el formato de fecha corta —d/m/a— y el nombre de los meses
 * en la variante que se lee de este lado.
 *
 * Ojo con dónde se usa: esto es para lo que ve EL CONTRATISTA. Los
 * documentos que salen para el cliente —la propuesta, el contrato, la
 * factura— y el formato del dinero siguen en `en-US` a propósito: los lee
 * alguien más, y su idioma no es el de esta interfaz.
 */
export const LOCALES: Record<Idioma, string> = {
    es: 'es-MX',
    en: 'en-US',
};

interface Contexto {
    idioma: Idioma;
    cambiarIdioma: (i: Idioma) => void;
    t: Traducir;
    /** Para fechas y números de la interfaz. Ver `LOCALES`. */
    locale: string;
}

const IdiomaContext = React.createContext<Contexto | undefined>(undefined);

/**
 * `forzar` fija el idioma de un subárbol y desconecta el selector dentro de él.
 *
 * Existe por la propuesta pública. Ese documento no lo lee quien eligió el
 * idioma —lo lee el cliente, en su navegador, con su localStorage—, así que
 * `detectarIdioma()` es justo lo que NO se quiere: hacía que el idioma del
 * documento lo decidiera el ordenador de quien lo abre. El idioma correcto
 * viaja guardado en la propia propuesta (`ProposalSettings.idioma`), y este
 * parámetro es cómo se impone sobre lo que diga el navegador.
 *
 * Anida: el proveedor de `index.tsx` sigue mandando en toda la app, y solo el
 * trozo envuelto por un proveedor con `forzar` se sale de esa regla.
 *
 * OJO: por defecto envuelve una PÁGINA entera, no un trozo de una. El efecto
 * de abajo escribe `<html lang>`, que es de toda la página y no del subárbol,
 * así que meter uno forzado dentro de una pantalla normal dejaría el documento
 * anunciado en un idioma y el resto de la pantalla escrito en otro — y un
 * lector de pantalla leería la mitad con la fonética equivocada.
 *
 * Para el caso legítimo de un trozo —el documento de impresión vive dentro del
 * editor, y sale en el idioma del CLIENTE, no en el de quien lo está
 * editando— está `alcance="subarbol"`: traduce igual y no toca `<html lang>`,
 * que sigue describiendo la página que de verdad se está mirando.
 */
export const IdiomaProvider: React.FC<{
    children: React.ReactNode;
    forzar?: Idioma;
    alcance?: 'pagina' | 'subarbol';
}> = ({ children, forzar, alcance = 'pagina' }) => {
    const [propio, setPropio] = React.useState<Idioma>(detectarIdioma);
    const idioma = forzar ?? propio;

    const cambiarIdioma = React.useCallback((i: Idioma) => {
        // Con el idioma forzado, cambiarlo no significa nada: el documento se
        // lee como se mandó, no como lo prefiera quien lo abre.
        if (forzar) return;
        setPropio(i);
        try { window.localStorage?.setItem(LLAVE_GUARDADA, i); } catch { /* modo privado */ }
        // El atributo `lang` no es decorativo: de él dependen la separación
        // silábica, las comillas del navegador y lo que anuncia un lector de
        // pantalla. Sin actualizarlo, un lector leería el español con fonética
        // inglesa.
        if (typeof document !== 'undefined') document.documentElement.lang = i;
    }, [forzar]);

    React.useEffect(() => {
        if (alcance === 'subarbol') return;
        if (typeof document !== 'undefined') document.documentElement.lang = idioma;
    }, [idioma, alcance]);

    const t = React.useCallback(
        (clave: Clave, vars?: Record<string, string | number>) => traducir(idioma, clave, vars),
        [idioma],
    );

    const valor = React.useMemo(
        () => ({ idioma, cambiarIdioma, t, locale: LOCALES[idioma] }),
        [idioma, cambiarIdioma, t],
    );
    return <IdiomaContext.Provider value={valor}>{children}</IdiomaContext.Provider>;
};

/**
 * Sin proveedor NO se revienta: se cae a español y se avisa en desarrollo.
 *
 * Antes esto lanzaba, que es la costumbre en React para cazar un cableado mal
 * hecho. Deja de compensar cuando 140 componentes dependen del hook: un
 * subárbol que se monte fuera del proveedor —un portal, un documento para
 * imprimir, una vista cargada aparte— pasa de enseñar texto en inglés a no
 * enseñar nada, y el contratista se queda con la pantalla en blanco.
 *
 * El aviso mantiene el error visible para quien programa, que es a quien le
 * toca arreglarlo, sin castigar a quien está usando la app.
 */
export function useIdioma(): Contexto {
    const c = React.useContext(IdiomaContext);
    if (c) return c;
    if (import.meta.env.DEV) {
        console.warn('[i18n] useIdioma fuera de <IdiomaProvider>: se usa español y el selector no afecta a este subárbol.');
    }
    return SIN_PROVEEDOR;
}

/** Español fijo. `cambiarIdioma` no hace nada: sin proveedor no hay estado que mover. */
const SIN_PROVEEDOR: Contexto = {
    idioma: 'es',
    cambiarIdioma: () => { },
    t: (clave, vars) => traducir('es', clave, vars),
    locale: LOCALES.es,
};
